import React from "react";
import { Button, Spin } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import Events from "components/events";
import Icons from "components/icons";
import { Localize } from "components/service";
import useCheckQrPosterExport from "components/events/hooks/use-check-qr-poster-export";
import useCheckQrRegenerateConfirmation from "components/events/hooks/use-check-qr-regenerate-confirmation";
import CheckQrRegenerateModal from "components/events/fields/check-qr-regenerate-modal";
import StaffCheckInProvenanceField from "./staff-check-in-provenance-field";
import { ENABLE_QR_OUTDATED_CHECK } from "components/events/helpers/event-check-qr-policy";
import {
    STAFF_CHECK_QR_STATUS,
    isStaffCheckQrLoading,
} from "../helpers/staff-check-qr-helper";


const StaffManagementCheckInQrField = ({ event, checkQr }) => {
    const eventID = event?.id;
    const qrUrl = checkQr ? checkQr.qrUrl : event?.getCheckQrCodeUrl;
    const status = checkQr?.status || (
        qrUrl ? STAFF_CHECK_QR_STATUS.READY : STAFF_CHECK_QR_STATUS.MISSING
    );
    const ready = status === STAFF_CHECK_QR_STATUS.READY;
    const missing = status === STAFF_CHECK_QR_STATUS.MISSING;
    const outdated = ENABLE_QR_OUTDATED_CHECK && [
        STAFF_CHECK_QR_STATUS.OUTDATED,
        STAFF_CHECK_QR_STATUS.REGENERATING,
        STAFF_CHECK_QR_STATUS.REGENERATION_FAILED,
    ].includes(status);
    const loading = isStaffCheckQrLoading(status);
    const actionDisabled = loading || checkQr?.locationSaving || !checkQr?.canGenerate;
    const showAvailableQr = Boolean(qrUrl) && (!ENABLE_QR_OUTDATED_CHECK || ready);

    const { exportLoading, exportPoster } = useCheckQrPosterExport({
        eventID,
        qrUrl,
        qrGeneratedAt: event?.check_qr_generated_at,
        eventTitle: event?.title,
        disabled: !ready || checkQr?.locationSaving,
    });
    const downloadPng = () => exportPoster(
        "png",
        Events.Helpers.CheckQr.downloadCheckQrPng
    );
    const downloadPdf = () => exportPoster(
        "pdf",
        Events.Helpers.CheckQr.downloadCheckQrPdf
    );
    const printQr = () => exportPoster(
        "print",
        Events.Helpers.CheckQr.printCheckQr
    );

    const { requestGeneration, confirmationProps } = useCheckQrRegenerateConfirmation({
        eventID,
        qrUrl,
        disabled: actionDisabled || !!exportLoading,
        onGenerate: checkQr?.generateQr,
    });

    const renderQrState = () => {
        if (status === STAFF_CHECK_QR_STATUS.GENERATING)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state" role="status">
                    <Spin size="small" />
                    <span><Localize>STAFF_MANAGEMENT.QrState_Generating</Localize></span>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.REGENERATING)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state" role="status">
                    <Spin size="small" />
                    <span><Localize>STAFF_MANAGEMENT.QrState_Regenerating</Localize></span>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.GENERATION_FAILED)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--error">
                    <span role="alert"><Localize>STAFF_MANAGEMENT.QrState_GenerationFailed</Localize></span>
                    <Button type="primary" disabled={ actionDisabled } onClick={ requestGeneration }>
                        <Localize>STAFF_MANAGEMENT.Action_RetryQrGeneration</Localize>
                    </Button>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.REGENERATION_FAILED)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--error">
                    <span role="alert"><Localize>STAFF_MANAGEMENT.QrState_RegenerationFailed</Localize></span>
                    <Button type="primary" disabled={ actionDisabled } onClick={ requestGeneration }>
                        <Localize>STAFF_MANAGEMENT.Action_RetryQrGeneration</Localize>
                    </Button>
                </div>
            );
        }

        if (ENABLE_QR_OUTDATED_CHECK && status === STAFF_CHECK_QR_STATUS.OUTDATED)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--warning">
                    <span role="alert"><Localize>STAFF_MANAGEMENT.QrState_Outdated</Localize></span>
                    { !checkQr?.canGenerate &&
                        <span className="staff-management-check-in-qr__state-hint">
                            <Localize>STAFF_MANAGEMENT.QrState_SaveValidSetup</Localize>
                        </span>
                    }
                    <Button type="primary" disabled={ actionDisabled } onClick={ requestGeneration }>
                        <Localize>STAFF_MANAGEMENT.Action_RegenerateQr</Localize>
                    </Button>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.MISSING)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--missing">
                    <strong><Localize>STAFF_MANAGEMENT.QrState_NoQrTitle</Localize></strong>
                    <span><Localize br={ <br /> }>STAFF_MANAGEMENT.QrState_NoQrDescription</Localize></span>
                </div>
            );
        }

        return null;
    };

    return (
        <section className="staff-management-check-in-qr">
            <h2><Localize>STAFF_MANAGEMENT.Section_CheckInQr</Localize></h2>

            { qrUrl && <StaffCheckInProvenanceField event={ event } kind="qr" loading={ loading } /> }

            { showAvailableQr ?
                <div className="staff-management-check-in-qr__available">
                    <div className="staff-management-check-in-qr__image">
                        <img src={ qrUrl } alt="QR Code" />
                    </div>

                    <div className="d-flex flex-column staff-management-check-in-qr__actions">
                        <Button
                            className="btn-icon-text"
                            disabled={ !ready || checkQr?.locationSaving || !!exportLoading }
                            loading={ exportLoading === "png" }
                            onClick={ downloadPng }>
                            <Icons.Download />
                            <Localize>CHECK_QR.Button_DownloadPng</Localize>
                        </Button>
                        <Button
                            className="btn-icon-text"
                            disabled={ !ready || checkQr?.locationSaving || !!exportLoading }
                            loading={ exportLoading === "pdf" }
                            onClick={ downloadPdf }>
                            <Icons.Download />
                            <Localize>CHECK_QR.Button_DownloadPdf</Localize>
                        </Button>
                        <Button
                            className="btn-icon-text staff-management-check-in-qr__print"
                            disabled={ !ready || checkQr?.locationSaving || !!exportLoading }
                            loading={ exportLoading === "print" }
                            onClick={ printQr }>
                            <Icons.StaffPrinter />
                            <Localize>CHECK_QR.Button_Print</Localize>
                        </Button>
                    </div>
                </div> :
                <div className={ `staff-management-check-in-qr__unavailable${ missing ? " staff-management-check-in-qr__unavailable--missing" : "" }${ outdated ? " staff-management-check-in-qr__unavailable--outdated" : "" }` }>
                    { qrUrl ?
                        <div className="staff-management-check-in-qr__image staff-management-check-in-qr__image--outdated">
                            <img src={ qrUrl } alt="" aria-hidden="true" />
                            <span><Localize>STAFF_MANAGEMENT.QrState_OutdatedLabel</Localize></span>
                        </div> :
                        <div className="staff-management-check-in-qr__empty">
                            <Icons.StaffNoQrCode aria-hidden="true" />
                        </div>
                    }

                    { renderQrState() }
                </div>
            }

            { showAvailableQr && renderQrState() }

            { qrUrl &&
                <p className={ loading ? "staff-management-check-in-qr__description--muted" : "" }>
                    <Localize>STAFF_MANAGEMENT.Text_QrDescription</Localize>
                </p>
            }

            { ready && qrUrl &&
                <div className="staff-management-check-in-qr__regenerate">
                    <span><Localize>STAFF_MANAGEMENT.Text_RevokeQrPrompt</Localize></span>
                    <Button
                        type="link"
                        className="btn-text-link btn-text-link--underline staff-management-check-in-qr__regenerate-button"
                        icon={ <ReloadOutlined aria-hidden="true" /> }
                        disabled={ actionDisabled || !!exportLoading }
                        onClick={ requestGeneration }>
                        <Localize>STAFF_MANAGEMENT.Action_RegenerateQr</Localize>
                    </Button>
                </div>
            }

            <CheckQrRegenerateModal { ...confirmationProps } />
        </section>
    );
};

export default StaffManagementCheckInQrField;
